<template>
  <div class="consent-required">
    <div class="container">
      <div class="content">
        <div class="icon">
          <i class="fas fa-shield-alt"></i>
        </div>

        <h1>Consent Required</h1>

        <div class="explanation">
          <p>
            To provide you with the best cybersecurity competency assessment
            experience, we need your consent to process your data. This
            includes:
          </p>

          <div class="data-usage">
            <h3>How We Use Your Data</h3>
            <ul>
              <li>
                <strong>Survey Responses:</strong> To generate your personalized
                competency report
              </li>
              <li>
                <strong>Progress Tracking:</strong> To save your survey progress
                and allow you to continue later
              </li>
              <li>
                <strong>Account Information:</strong> To provide you with secure
                access to your results
              </li>
              <li>
                <strong>Research Data:</strong> To improve cybersecurity
                education and training programs
              </li>
            </ul>
          </div>

          <div class="data-protection">
            <h3>Data Protection</h3>
            <ul>
              <li>Your data is encrypted and stored securely</li>
              <li>
                We do not share your personal information with third parties
              </li>
              <li>You can request deletion of your data at any time</li>
              <li>Data is retained only for the specified periods</li>
            </ul>
          </div>

          <div class="retention-policy">
            <h3>Data Retention Policy</h3>
            <div class="retention-grid">
              <div class="retention-item">
                <span class="period">2 Years</span>
                <span class="type">Survey Data</span>
              </div>
              <div class="retention-item">
                <span class="period">30 Days</span>
                <span class="type">Progress Data</span>
              </div>
              <div class="retention-item">
                <span class="period">5 Years</span>
                <span class="type">Consent Records</span>
              </div>
            </div>
          </div>
        </div>

        <div class="actions">
          <button @click="goBack" class="btn-secondary">
            <i class="fas fa-arrow-left"></i>
            Go Back
          </button>
          <button @click="giveConsent" class="btn-primary">
            <i class="fas fa-check"></i>
            Give Consent
          </button>
        </div>

        <div class="contact-info">
          <p>
            If you have questions about data processing, please contact us at:
            <a href="mailto:privacy@cyberireland.ie">privacy@cyberireland.ie</a>
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { useRouter } from "vue-router";

const router = useRouter();

const goBack = () => {
  router.go(-1);
};

const giveConsent = () => {
  // Store consent
  localStorage.setItem("surveyConsent", "true");
  localStorage.setItem("consentDate", new Date().toISOString());

  // Redirect to home page
  router.push("/");
};
</script>

<style scoped>
.consent-required {
  min-height: 100vh;
  background: #dae2e9;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.container {
  max-width: 800px;
  width: 100%;
}

.content {
  background: #fff;
  border-radius: 16px;
  padding: 40px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  text-align: center;
}

.icon {
  font-size: 4rem;
  color: #005461;
  margin-bottom: 20px;
}

h1 {
  color: #005461;
  margin-bottom: 30px;
  font-size: 2.5rem;
  font-weight: 700;
}

.explanation {
  text-align: left;
  margin-bottom: 30px;
}

.explanation p {
  font-size: 1.1rem;
  line-height: 1.6;
  color: #222;
  margin-bottom: 25px;
}

.data-usage,
.data-protection,
.retention-policy {
  margin-bottom: 25px;
}

h3 {
  color: #005461;
  font-size: 1.3rem;
  margin-bottom: 15px;
  font-weight: 600;
}

ul {
  list-style: none;
  padding: 0;
}

li {
  padding: 8px 0;
  line-height: 1.5;
  color: #222;
  position: relative;
  padding-left: 20px;
}

li:before {
  content: "✓";
  color: #005461;
  font-weight: bold;
  position: absolute;
  left: 0;
}

.retention-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 15px;
  margin-top: 15px;
}

.retention-item {
  background: #c3d0db;
  padding: 15px;
  border-radius: 8px;
  text-align: center;
  border: 2px solid #dae2e9;
}

.retention-item .period {
  display: block;
  font-size: 1.2rem;
  font-weight: 700;
  color: #005461;
  margin-bottom: 5px;
}

.retention-item .type {
  display: block;
  font-size: 0.9rem;
  color: #222;
}

.actions {
  display: flex;
  gap: 15px;
  justify-content: center;
  margin: 30px 0;
  flex-wrap: wrap;
}

.btn-primary,
.btn-secondary {
  padding: 12px 24px;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 140px;
  justify-content: center;
}

.btn-primary {
  background: #005461;
  color: white;
}

.btn-primary:hover {
  background: #003d49;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 84, 97, 0.15);
}

.btn-secondary {
  background: transparent;
  color: #005461;
  border: 2px solid #005461;
}

.btn-secondary:hover {
  background: rgba(0, 84, 97, 0.08);
  color: #003d49;
  border-color: #003d49;
  transform: translateY(-2px);
}

.contact-info {
  margin-top: 30px;
  padding-top: 20px;
  border-top: 1px solid #eee;
}

.contact-info p {
  color: #222;
  font-size: 0.9rem;
}

.contact-info a {
  color: #005461;
  text-decoration: underline;
  font-weight: 600;
}

.contact-info a:hover {
  color: #003d49;
  text-decoration: underline;
}

@media (max-width: 768px) {
  .content {
    padding: 30px 20px;
  }

  h1 {
    font-size: 2rem;
  }

  .actions {
    flex-direction: column;
    align-items: center;
  }

  .btn-primary,
  .btn-secondary {
    width: 100%;
    max-width: 250px;
  }

  .retention-grid {
    grid-template-columns: 1fr;
  }
}
</style>
